import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router, Redirect } from 'react-router-dom';
import { LocalizeProvider, Translate } from 'react-localize-redux';
import { Spin, Alert } from "antd";
import bindAllOfThis from "src/utils/BindThisHelper";

import registerServiceWorker from 'src/core/registerServiceWorker';
import Routes from 'src/core/Routes';
import MultilingualLoader from 'src/core/MultilingualLoader';
import DebugUtil from "src/utils/DebugUtil";
import RouteLoader from 'src/core/ConfigureRouter';
import ConfiguredReduxStore from 'src/core/ConfigureRedux';
import History from 'src/core/ConfigureHistory'; // Generates a default history state
import TelemetryProvider from 'src/providers/TelemetryProvider';
import { datadogRum } from '@datadog/browser-rum';

import 'src/core/ConfigureFonts'; // This will run the fonts configurator
import './App.less';

interface AppState {
    multiLingualLoading: boolean;
    reduxLoading: boolean;
    isUserLoggedIn: boolean;
    isLandscape: boolean;
}

/** This class is VERY special. It handles loading react, the routes and any 'on startup' logic */
class App extends React.Component<{}, AppState> {
    constructor(props: AppState) {
        super(props);
        bindAllOfThis(this, App.prototype);

        this.state = {
            multiLingualLoading: true,
            reduxLoading: true,
            isUserLoggedIn: false,
            isLandscape: true,
        };
        if (DebugUtil.isDebugEnabled()) {
            History.listen((location, action) => {
                console.log('Route has changed', location);
            });
        }
    }

    componentDidMount() {
        datadogRum.init({
            applicationId: 'cce3256f-2f55-4655-b1f6-05e5f8a351c8',
            clientToken: 'pub33e256446f1b36b7b80047baf5fdf569',
            site: 'datadoghq.com',
            service:'galaxy',
            env: `galaxy-${deploymentEnvironment}`,
            // Specify a version number to identify the deployed version of your application in Datadog
            // version: '1.0.0',
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel:'mask-user-input'
        });

        datadogRum.startSessionReplayRecording();

        window.addEventListener("resize", this.toggleOrientation);
        // Have to manually call it the first time
        this.toggleOrientation();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.toggleOrientation);
    }

    private toggleOrientation() {
        this.setState({ isLandscape: window.matchMedia("(orientation:landscape)").matches });
    }

    componentDidCatch(e: Error) {
        // Errors are stupid and wont stringify properly
        var error: string = JSON.stringify({
            name: e.name,
            message: e.message,
            stack: e.stack
        });
        History.push({
            pathname: DebugUtil.isDebugEnabled() ? Routes.ERROR_PAGE : Routes.PROD_ERROR_PAGE,
            state: { errors: error }
        });
    }

    multiLingualComplete() {
        this.setState({
            multiLingualLoading: false
        });
    }

    reduxComplete(result: boolean) {
        this.setState({
            reduxLoading: false,
            isUserLoggedIn: result
        });
    }

    renderAlert() {
        if (this.state.isLandscape != null && !this.state.isLandscape) {
            return <Alert
                key="Banner"
                banner={true}
                closable={true}
                type="warning"
                message={<h3><Translate id="common.orientationWarningHeader" /></h3>}
                description={<p><Translate id="common.orientationWarningBody" /></p>}
            />;
        }
        return null;
    }

    render() {
        if (this.state.multiLingualLoading) {
            return <div className="App">
                <MultilingualLoader onComplete={this.multiLingualComplete} />
                <Spin className={"centered very-large-spinner"} />
            </div>;
        }

        return <div className="App">
            <MultilingualLoader />
            {this.renderAlert()}
            {RouteLoader}
        </div>;
    }
}

ReactDOM.render(
    <Provider store={ConfiguredReduxStore as any}>
        <LocalizeProvider store={ConfiguredReduxStore}>
            <Router history={History}>
                <TelemetryProvider instrumentationKey={applicationInsightsKey}>
                    <App />
                </TelemetryProvider>
            </Router>
        </LocalizeProvider>
    </Provider>,

    document.getElementById('root') as HTMLElement
);
registerServiceWorker();



// WEBPACK FOOTER //
// ./src/app.tsx